import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Menu, X, User, Camera, LogOut, Calendar, Image } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, photographer, isAuthenticated, isPhotographer, logout, photographerLogout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const navLinks = [
    { href: '/', label: 'Home' },
    { href: '/booking', label: 'Book Now' },
    { href: '/rentals', label: 'Rent Snakes' },
    { href: '/gallery', label: 'Gallery' },
    { href: 'mailto:business1.marcos@gmail.com?subject=Slither Sessions Inquiry', label: 'Contact Us', isExternal: true },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handlePhotographerLogout = async () => {
    await photographerLogout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
            <span className="text-2xl font-bold gradient-text font-['Playfair_Display']">
              Slither Sessions
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              link.isExternal ? (
                <a
                  key={link.href}
                  href={link.href}
                  className={`nav-link text-sm font-medium uppercase tracking-widest transition-colors text-muted-foreground hover:text-primary`}
                  data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`nav-link text-sm font-medium uppercase tracking-widest transition-colors ${
                    isActive(link.href) ? 'text-primary' : 'text-muted-foreground hover:text-primary'
                  }`}
                  data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  {link.label}
                </Link>
              )
            ))}
          </div>

          {/* Desktop Auth Section */}
          <div className="hidden md:flex items-center space-x-4">
            {isPhotographer && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center space-x-2" data-testid="photographer-menu">
                    <Camera className="w-4 h-4 text-primary" />
                    <span className="text-sm">{photographer?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-card border-border">
                  <DropdownMenuItem onClick={() => navigate('/photographer/dashboard')} data-testid="photographer-dashboard-link">
                    <Calendar className="w-4 h-4 mr-2" />
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handlePhotographerLogout} data-testid="photographer-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center space-x-2" data-testid="user-menu">
                    <User className="w-4 h-4 text-primary" />
                    <span className="text-sm">{user?.name}</span>
                    {user?.is_member && (
                      <span className="member-badge text-xs">Member</span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-card border-border">
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="user-dashboard-link">
                    <Calendar className="w-4 h-4 mr-2" />
                    My Bookings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="user-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center space-x-3">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  className="text-sm"
                  data-testid="login-btn"
                >
                  Sign In
                </Button>
                <Button
                  onClick={() => navigate('/register')}
                  className="btn-primary text-sm"
                  data-testid="register-btn"
                >
                  Join Now
                </Button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] bg-background border-border">
              <div className="flex flex-col h-full pt-8">
                {/* Mobile Nav Links */}
                <div className="flex flex-col space-y-4">
                  {navLinks.map((link) => (
                    link.isExternal ? (
                      <a
                        key={link.href}
                        href={link.href}
                        onClick={() => setIsOpen(false)}
                        className="text-lg font-medium py-2 transition-colors text-foreground hover:text-primary"
                        data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}`}
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        key={link.href}
                        to={link.href}
                        onClick={() => setIsOpen(false)}
                        className={`text-lg font-medium py-2 transition-colors ${
                          isActive(link.href) ? 'text-primary' : 'text-foreground hover:text-primary'
                        }`}
                        data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}`}
                      >
                        {link.label}
                      </Link>
                    )
                  ))}
                </div>

                {/* Divider */}
                <div className="my-6 border-t border-border" />

                {/* Mobile Auth Section */}
                <div className="flex flex-col space-y-4">
                  {isPhotographer && (
                    <>
                      <div className="flex items-center space-x-2 text-primary">
                        <Camera className="w-5 h-5" />
                        <span className="font-medium">{photographer?.name}</span>
                      </div>
                      <Link
                        to="/photographer/dashboard"
                        onClick={() => setIsOpen(false)}
                        className="text-muted-foreground hover:text-foreground"
                        data-testid="mobile-photographer-dashboard"
                      >
                        <Calendar className="w-4 h-4 inline mr-2" />
                        Dashboard
                      </Link>
                      <button
                        onClick={() => {
                          handlePhotographerLogout();
                          setIsOpen(false);
                        }}
                        className="text-left text-muted-foreground hover:text-foreground"
                        data-testid="mobile-photographer-logout"
                      >
                        <LogOut className="w-4 h-4 inline mr-2" />
                        Logout (Photographer)
                      </button>
                    </>
                  )}
                  
                  {isAuthenticated ? (
                    <>
                      <div className="flex items-center space-x-2">
                        <User className="w-5 h-5 text-primary" />
                        <span className="font-medium">{user?.name}</span>
                        {user?.is_member && (
                          <span className="member-badge text-xs">Member</span>
                        )}
                      </div>
                      <Link
                        to="/dashboard"
                        onClick={() => setIsOpen(false)}
                        className="text-muted-foreground hover:text-foreground"
                        data-testid="mobile-user-dashboard"
                      >
                        <Calendar className="w-4 h-4 inline mr-2" />
                        My Bookings
                      </Link>
                      <button
                        onClick={() => {
                          handleLogout();
                          setIsOpen(false);
                        }}
                        className="text-left text-muted-foreground hover:text-foreground"
                        data-testid="mobile-user-logout"
                      >
                        <LogOut className="w-4 h-4 inline mr-2" />
                        Logout
                      </button>
                    </>
                  ) : (
                    <>
                      <Link
                        to="/login"
                        onClick={() => setIsOpen(false)}
                        className="text-foreground font-medium"
                        data-testid="mobile-login"
                      >
                        Sign In
                      </Link>
                      <Link
                        to="/register"
                        onClick={() => setIsOpen(false)}
                        className="btn-primary text-center"
                        data-testid="mobile-register"
                      >
                        Join Now
                      </Link>
                    </>
                  )}
                </div>

                {/* Photographer Login Link */}
                {!isPhotographer && (
                  <div className="mt-auto pb-8">
                    <Link
                      to="/photographer/login"
                      onClick={() => setIsOpen(false)}
                      className="flex items-center space-x-2 text-muted-foreground hover:text-primary"
                      data-testid="mobile-photographer-login"
                    >
                      <Camera className="w-4 h-4" />
                      <span className="text-sm">Photographer Portal</span>
                    </Link>
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
